/**
 * Track0tic Control Portal Layout
 * Corporate Navy Enterprise Theme - Structured & Formal
 */
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTrackOticAuth, getBearerToken } from '@/context/TrackOticAuthContext';
import { ControlThemeProvider, useControlTheme } from '@/contexts/ControlThemeContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Building2, Users, Wifi, Bell, 
  FileText, CreditCard, LogOut, ChevronLeft, ChevronRight,
  LayoutDashboard, Settings, Truck, Package, Thermometer,
  Sun, Moon, Plus, UserPlus, Cpu, Menu, Search, Send, ClipboardList, QrCode
} from 'lucide-react';
import { TrackOticBrand } from '@/components/Brand';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/control/dashboard' },
  // ===== SETUP FLOW (locked in order until prereqs satisfied) =====
  { kind: 'section', label: 'Setup' },
  { id: 'devices', label: 'Devices', icon: Wifi, path: '/control/devices', hint: 'Register IMEIs from manufacturer', requires: null },
  { id: 'device-registry', label: 'Device Registry', icon: ClipboardList, path: '/control/device-registry', hint: 'Inventory of all received devices', requires: 'has_devices' },
  { id: 'tenants', label: 'Customers', icon: Building2, path: '/control/tenants', hint: 'Register customer companies (auto-creates admin)', requires: null },
  { id: 'onboarding', label: 'Onboarding', icon: Cpu, path: '/control/onboarding', hint: 'Guided 5-step wizard — needs at least 1 device + 1 customer', requires: 'has_devices_and_customers' },
  { id: 'device-dispatch', label: 'Dispatch to Customer', icon: Send, path: '/control/device-dispatch', hint: 'Send registered devices to a customer', requires: 'has_devices_and_customers' },
  // ===== OPERATIONS =====
  { kind: 'section', label: 'Operations' },
  { id: 'fleet', label: 'Fleet Management', icon: Truck, path: '/control/fleet' },
  { id: 'shipments', label: 'Shipments', icon: Package, path: '/control/shipments' },
  { id: 'cold-chain', label: 'Cold Chain', icon: Thermometer, path: '/control/cold-chain' },
  // ===== MONITORING =====
  { kind: 'section', label: 'Monitoring' },
  { id: 'alerts', label: 'Alerts', icon: Bell, path: '/control/alerts' },
  { id: 'audit', label: 'Audit Logs', icon: FileText, path: '/control/audit' },
  // ===== ADMIN =====
  { kind: 'section', label: 'Admin' },
  { id: 'platform-users', label: 'Platform Users', icon: Users, path: '/control/users', hint: 'Track0tic internal staff (admins, support, engineers)' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/control/billing' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/control/settings' },
];

const quickActions = [
  { id: 'add-device', label: 'Register Device', icon: Cpu, path: '/control/devices?action=register' },
  { id: 'add-customer', label: 'Add Customer', icon: UserPlus, path: '/control/tenants?action=register' },
  { id: 'new-shipment', label: 'New Shipment', icon: Package, path: '/control/shipments?action=new' },
];

function ControlLayoutContent() {
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [setupState, setSetupState] = useState({ devices: 0, customers: 0, loaded: false });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useTrackOticAuth();
  const { theme, toggleTheme, isDark } = useControlTheme();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/control/login', { replace: true });
    }
     
  }, [user, loading, navigate]);

  // Poll setup state so Setup section unlocks dynamically as user makes progress.
  useEffect(() => {
    if (!user) return;
    const token = getBearerToken();
    const headers = { Authorization: `Bearer ${token}` };
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    let cancelled = false;

    const load = async () => {
      try {
        const [unassignedRes, clientsRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/control/onboarding/unassigned-devices`, { headers })
            .then(r => r.ok ? r.json() : []).catch(() => []),
          fetch(`${API_BASE}/api/v1/control/onboarding/clients`, { headers })
            .then(r => r.ok ? r.json() : []).catch(() => []),
        ]);
        if (!cancelled) {
          setSetupState({
            devices: Array.isArray(unassignedRes) ? unassignedRes.length : 0,
            customers: Array.isArray(clientsRes) ? clientsRes.length : 0,
            loaded: true,
          });
        }
      } catch (_e) { /* silent — Setup just stays locked */ }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, location.pathname]);

  useEffect(() => {
    setNotifications([
      { id: 1, message: 'Temperature breach detected in Device TRK-001', type: 'critical', time: '2 min ago' },
      { id: 2, message: 'New customer registered: Medplus Pharmacy', type: 'info', time: '15 min ago' },
      { id: 3, message: 'Fleet vehicle VH-102 went offline', type: 'warning', time: '1 hr ago' },
    ]);
     
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/control/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Corporate Navy Theme Colors
  const colors = isDark ? {
    bgMain: 'bg-slate-900',
    bgSidebar: 'bg-slate-800',
    bgHeader: 'bg-slate-800',
    bgCard: 'bg-slate-800',
    border: 'border-slate-700',
    textPrimary: 'text-white',
    textSecondary: 'text-blue-300',
    textMuted: 'text-slate-300',
    accent: 'bg-blue-600',
    accentHover: 'hover:bg-blue-700',
    accentText: 'text-blue-300',
    navActive: 'bg-blue-600/30 text-white border-l-4 border-blue-400',
    navHover: 'hover:bg-slate-700/60 hover:text-white',
  } : {
    bgMain: 'bg-slate-50',
    bgSidebar: 'bg-white',
    bgHeader: 'bg-white',
    bgCard: 'bg-white',
    border: 'border-slate-200',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-blue-700',
    textMuted: 'text-slate-700',
    accent: 'bg-blue-600',
    accentHover: 'hover:bg-blue-700',
    accentText: 'text-blue-700',
    navActive: 'bg-blue-50 text-blue-700 border-l-4 border-blue-600',
    navHover: 'hover:bg-slate-100 hover:text-slate-900',
  };

  const themeClass = isDark ? 'control-theme-dark' : 'control-theme-light';

  return (
    <div className={cn("min-h-screen flex", themeClass, colors.bgMain)}>
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 h-full z-40 transition-all duration-300 border-r shadow-sm",
          colors.bgSidebar, colors.border,
          collapsed ? "w-16" : "w-60"
        )}
      >
        {/* Logo Header */}
        <div className={cn("h-16 flex items-center px-4 border-b", colors.border)}>
          {!collapsed ? (
            <div className="flex items-center">
              <div>
                <TrackOticBrand size="default" showTagline className={cn(colors.textPrimary)} />
                <p className={cn("text-[10px] uppercase tracking-wider mt-0.5", colors.textMuted)}>Control Portal</p>
              </div>
            </div>
          ) : (
            <div className="w-full text-center">
              <span className={cn("font-bold text-base", colors.textPrimary)}>
                T<span className="text-red-500">0</span>
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ height: 'calc(100vh - 180px)' }}>
          {menuItems.map((item, idx) => {
            // Section divider header
            if (item.kind === 'section') {
              if (collapsed) {
                return (
                  <div key={`sec-${idx}`} className={cn("mt-3 mb-1 h-px mx-2", isDark ? "bg-slate-700" : "bg-slate-200")} />
                );
              }
              return (
                <div
                  key={`sec-${idx}`}
                  className={cn(
                    "text-[10px] font-bold uppercase tracking-wider px-3 pt-4 pb-1",
                    isDark ? "text-slate-400" : "text-slate-500"
                  )}
                >
                  {item.label}
                </div>
              );
            }
            const Icon = item.icon;
            const active = isActive(item.path);

            // Smart locking disabled by user request — every item is always navigable.
            // Hints are still surfaced as tooltips for context.
            const tooltip = collapsed ? item.label : (item.hint || undefined);

            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all",
                  active ? colors.navActive : cn(colors.textMuted, colors.navHover),
                  collapsed && "justify-center px-2"
                )}
                data-testid={`nav-${item.id}`}
                title={tooltip}
              >
                <Icon className={cn("w-5 h-5 flex-shrink-0", active && colors.accentText)} />
                {!collapsed && (
                  <span className="flex-1 truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "absolute -right-3 top-20 w-6 h-6 rounded-full flex items-center justify-center shadow-md border",
            isDark ? "bg-slate-700 border-slate-600 text-slate-300" : "bg-white border-slate-200 text-slate-600"
          )}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {/* User Section */}
        <div className={cn(
          "absolute bottom-0 left-0 right-0 p-4 border-t",
          colors.border, colors.bgSidebar
        )}>
          {!collapsed ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-sm shadow">
                  {user?.full_name?.charAt(0) || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-medium truncate", colors.textPrimary)}>
                    {user?.full_name || 'Administrator'}
                  </p>
                  <p className={cn("text-xs", colors.textMuted)}>Platform Admin</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className={cn("w-full justify-start text-red-500 hover:text-red-400 hover:bg-red-500/10")}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex justify-center py-2 text-red-500 hover:text-red-400"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "flex-1 transition-all duration-300",
        collapsed ? "ml-16" : "ml-60"
      )}>
        {/* Top Header */}
        <header className={cn(
          "h-16 border-b flex items-center justify-between px-6 sticky top-0 z-30 shadow-sm",
          colors.bgHeader, colors.border
        )}>
          <div className="flex items-center gap-4">
            {/* Breadcrumb or Page Title */}
            <div className={cn("text-lg font-semibold", colors.textPrimary)}>
              {menuItems.find(item => isActive(item.path))?.label || 'Dashboard'}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Quick Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  data-testid="quick-actions-btn"
                  title="Shortcuts to common actions"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Quick Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={cn(isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200")}>
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <DropdownMenuItem 
                      key={action.id}
                      onClick={() => navigate(action.path)}
                      className={cn("cursor-pointer", isDark ? "hover:bg-slate-700" : "hover:bg-slate-50")}
                    >
                      <Icon className={cn("w-4 h-4 mr-2", colors.accentText)} />
                      {action.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className={cn("relative", colors.textMuted, isDark ? "hover:bg-slate-700" : "hover:bg-slate-100")}
                  data-testid="notifications-btn"
                  title="Notifications & alerts"
                >
                  <Bell className="w-5 h-5" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                      {notifications.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={cn("w-80", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200")}>
                <div className={cn("px-3 py-2 font-semibold text-sm border-b", colors.textPrimary, colors.border)}>
                  Notifications
                </div>
                {notifications.length === 0 ? (
                  <div className={cn("px-3 py-6 text-center text-sm", colors.textMuted)}>No new notifications</div>
                ) : (
                  notifications.map((notif) => (
                    <DropdownMenuItem 
                      key={notif.id} 
                      className={cn("flex flex-col items-start gap-1 cursor-pointer py-3", isDark ? "hover:bg-slate-700" : "hover:bg-slate-50")}
                    >
                      <div className="flex items-start gap-2 w-full">
                        <div className={cn(
                          "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                          notif.type === 'critical' ? 'bg-red-500' : notif.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                        )} />
                        <div className="flex-1">
                          <span className={cn("text-sm", colors.textPrimary)}>{notif.message}</span>
                          <p className={cn("text-xs mt-0.5", colors.textMuted)}>{notif.time}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
                <DropdownMenuSeparator className={colors.border} />
                <DropdownMenuItem 
                  onClick={() => navigate('/control/alerts')}
                  className={cn("justify-center cursor-pointer font-medium", colors.accentText)}
                >
                  View All Alerts
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={cn(colors.textMuted, isDark ? "hover:bg-slate-700" : "hover:bg-slate-100")}
              data-testid="theme-toggle-btn"
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {/* User Avatar — opens menu with profile + logout */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-sm ml-2 shadow cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
                  )}
                  title={`Signed in as ${user?.full_name || 'User'}`}
                  data-testid="user-avatar-btn"
                  aria-label="User menu"
                >
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={cn("w-56", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200")}>
                <div className={cn("px-3 py-2 border-b", colors.border)}>
                  <p className={cn("font-medium text-sm", colors.textPrimary)}>{user?.full_name || 'User'}</p>
                  <p className={cn("text-xs", colors.textMuted)}>{user?.email}</p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate('/control/settings')}
                  className={cn("cursor-pointer", isDark ? "hover:bg-slate-700" : "hover:bg-slate-50")}
                  data-testid="user-menu-settings"
                >
                  <Settings className={cn("w-4 h-4 mr-2", colors.textMuted)} />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className={colors.border} />
                <DropdownMenuItem
                  onClick={() => { logout(); navigate('/control/login'); }}
                  className="cursor-pointer text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className={cn("min-h-[calc(100vh-4rem)] p-6", colors.bgMain)}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default function ControlLayout() {
  return (
    <ControlThemeProvider>
      <ControlLayoutContent />
    </ControlThemeProvider>
  );
}
